// src/pages/DelegatedAccessDashboard.jsx
// Dashboard collaborateur avec onglets : Collections, Artworks, Rapports, Vision

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { openVision } from "../utils/openVision";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import {
  Eye, Edit3, Plus, Trash2, Download, BarChart2, Cpu,
  LogOut, Package, ChevronRight, ChevronDown,
  AlertCircle, Search, RefreshCw, X, ChevronLeft,
  MapPin, FileText, Layers,
} from "lucide-react";

const C = {
  bg:"#fafaf8", white:"#fff", black:"#1a1a1a", gold:"#c9a961",
  border:"#e5e3df", muted:"#6b6b6b", caption:"#9a8c7e", error:"#b00020",
};
const PAGE_SIZE = 50;
const ARTWORK_FALLBACK = "/images/artworks/Vignette_oeuvre_Image_Not_Available.png";

const ROLE_KEYS = { team_member:"team_member", team_readonly:"team_readonly", expert:"expert", assistant:"assistant", client:"client" };

const TABS = [
  { key:"collections", labelKey:"delegatedAccess.tabs.collections", icon:Package },
  { key:"artworks", labelKey:"delegatedAccess.tabs.artworks", icon:Layers },
  { key:"locations", labelKey:"delegatedAccess.tabs.locations", icon:MapPin },
  { key:"reports", labelKey:"delegatedAccess.tabs.reports", icon:FileText },
  { key:"vision", labelKey:"delegatedAccess.tabs.vision", icon:Cpu },
];

function pickImage(a) {
  if (a.image_url) return a.image_url;
  try {
    const imgs = typeof a.images==="string" ? JSON.parse(a.images) : a.images;
    if (Array.isArray(imgs)&&imgs.length) {
      const p = imgs.find(x=>x?.isPrimary||x?.is_primary)||imgs[0];
      return typeof p==="string" ? p : (p?.url||p?.path||null);
    }
  } catch {}
  return null;
}

function PermBadge({ icon, label, active }) {
  if (!active) return null;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:11,
      fontWeight:600, letterSpacing:"0.04em", padding:"3px 8px",
      background:C.bg, border:`1px solid ${C.border}`, color:C.black }}>
      {icon} {label}
    </span>
  );
}

// ── Carte oeuvre ─────────────────────────────────────────
function ArtworkCard({ artwork, canEdit }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const img = pickImage(artwork) || ARTWORK_FALLBACK;
  return (
    <div onClick={() => navigate(`/artwork/${artwork.id}`)}
      style={{ background:C.white, border:`1px solid ${C.border}`, overflow:"hidden",
        transition:"border-color 0.15s, box-shadow 0.15s", cursor:"pointer" }}
      onMouseEnter={e=>{ e.currentTarget.style.borderColor="#c9b89a"; e.currentTarget.style.boxShadow="0 4px 12px rgba(0,0,0,0.08)"; }}
      onMouseLeave={e=>{ e.currentTarget.style.borderColor=C.border; e.currentTarget.style.boxShadow="none"; }}>
      <div style={{ position:"relative", paddingBottom:"75%", background:"#f0ede8" }}>
        <img src={img} alt={artwork.title||""} onError={e=>{e.target.src=ARTWORK_FALLBACK;}}
          style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover" }}/>
        {canEdit && (
          <button type="button"
            onClick={e=>{ e.stopPropagation(); navigate(`/artwork/${artwork.id}/edit`); }}
            style={{ position:"absolute", top:6, right:6, background:"rgba(255,255,255,0.92)",
              border:`1px solid ${C.border}`, padding:"3px 8px", fontSize:10,
              color:C.black, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", gap:3 }}>
            <Edit3 size={9}/> {t("delegatedAccess.edit")}
          </button>
        )}
      </div>
      <div style={{ padding:"10px 12px" }}>
        <p style={{ fontFamily:"Georgia,serif", fontSize:13, fontStyle:"italic", color:C.black,
          margin:"0 0 2px", overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis" }}>
          {artwork.title || t("delegatedAccess.untitled")}
        </p>
        <p style={{ fontSize:11, color:C.muted, margin:"0 0 1px", overflow:"hidden",
          whiteSpace:"nowrap", textOverflow:"ellipsis" }}>
          {artwork.artist||"—"}{artwork.year?`, ${artwork.year}`:""}
        </p>
        {artwork.medium&&<p style={{ fontSize:10, color:C.caption, margin:0,
          overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis" }}>{artwork.medium}</p>}
      </div>
    </div>
  );
}

// ── Onglet Collections ────────────────────────────────────
function TabCollections({ collections, collabEntry }) {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState(null);
  const [artworksByCol, setArtworksByCol] = useState({});
  const [loadingCol, setLoadingCol] = useState({});
  const [searchByCol, setSearchByCol] = useState({});
  const [pageByCol, setPageByCol] = useState({});
  const canEdit = collabEntry?.can_edit === true;

  const loadArtworks = useCallback(async (colId) => {
    if (artworksByCol[colId]) return;
    setLoadingCol(p => ({ ...p, [colId]: true }));
    const { data, error } = await supabase.rpc("get_collaborator_artworks", {
      p_collaborator_id: collabEntry.id,
      p_collection_id: colId,
    });
    if (error) console.warn("[TabCollections] RPC failed:", error.message);
    setArtworksByCol(p => ({ ...p, [colId]: data||[] }));
    setLoadingCol(p => ({ ...p, [colId]: false }));
  }, [artworksByCol, collabEntry?.id]);

  const toggle = (colId) => {
    const next = openId === colId ? null : colId;
    setOpenId(next);
    if (next) loadArtworks(next);
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
      {collections.length === 0 && (
        <div style={{ textAlign:"center", padding:"48px 0", color:C.caption }}>
          <Package size={28} style={{ marginBottom:12, opacity:0.4 }}/>
          <p style={{ fontFamily:"Georgia,serif", fontSize:17, color:C.black, marginBottom:8 }}>
            {t("delegatedAccess.noSharedCollections")}
          </p>
        </div>
      )}
      {collections.map(col => {
        const isOpen = openId === col.id;
        const artworks = artworksByCol[col.id] || [];
        const loading = loadingCol[col.id];
        const search = searchByCol[col.id] || "";
        const page = pageByCol[col.id] || 0;
        const filtered = search.trim()
          ? artworks.filter(a => (a.title||"").toLowerCase().includes(search.toLowerCase()) || (a.artist||"").toLowerCase().includes(search.toLowerCase()))
          : artworks;
        const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
        const pageItems = filtered.slice(page * PAGE_SIZE, (page+1) * PAGE_SIZE);

        return (
          <div key={col.id} style={{ background:C.white, border:`1px solid ${C.border}` }}>
            <button type="button" onClick={() => toggle(col.id)}
              style={{ width:"100%", display:"flex", alignItems:"center", gap:16,
                padding:"18px 24px", background:"none", border:"none", cursor:"pointer",
                borderBottom: isOpen ? `1px solid ${C.border}` : "none" }}>
              <div style={{ width:36, height:36, background:"#f5f3f0", flexShrink:0,
                display:"flex", alignItems:"center", justifyContent:"center" }}>
                <Package size={16} color={C.caption}/>
              </div>
              <div style={{ flex:1, textAlign:"left" }}>
                <p style={{ fontFamily:"Georgia,serif", fontSize:16, color:C.black, margin:0 }}>
                  {col.title || t("delegatedAccess.untitledCollection")}
                </p>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
                {artworksByCol[col.id] && <span style={{ fontSize:12, color:C.caption }}>{t("delegatedAccess.artworkCount", { count: artworks.length })}</span>}
                {canEdit && <span style={{ fontSize:11, color:C.gold, fontWeight:700 }}>{t("delegatedAccess.editable")}</span>}
                {isOpen ? <ChevronDown size={16} color={C.caption}/> : <ChevronRight size={16} color={C.caption}/>}
              </div>
            </button>

            {isOpen && (
              <div style={{ padding:"20px 24px" }}>
                {artworksByCol[col.id] && artworks.length > 6 && (
                  <div style={{ position:"relative", marginBottom:16, maxWidth:300 }}>
                    <Search size={13} color={C.caption} style={{ position:"absolute", left:9, top:"50%", transform:"translateY(-50%)" }}/>
                    <input value={search}
                      onChange={e => { setSearchByCol(p=>({...p,[col.id]:e.target.value})); setPageByCol(p=>({...p,[col.id]:0})); }}
                      placeholder={t("delegatedAccess.search")}
                      style={{ width:"100%", padding:"7px 10px 7px 28px", border:`1px solid ${C.border}`,
                        fontSize:13, color:C.black, background:C.bg, outline:"none",
                        boxSizing:"border-box", fontFamily:"system-ui,sans-serif" }}/>
                  </div>
                )}
                {loading && <div style={{ display:"flex", alignItems:"center", gap:8, color:C.muted, fontSize:13, padding:"20px 0" }}><RefreshCw size={14} style={{ animation:"spin 1s linear infinite" }}/> {t("delegatedAccess.loading")}</div>}
                {!loading && filtered.length === 0 && <p style={{ fontSize:13, color:C.caption, textAlign:"center", padding:"24px 0" }}>{t("delegatedAccess.noArtwork")}</p>}
                {!loading && pageItems.length > 0 && (
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:14 }}>
                    {pageItems.map(a => <ArtworkCard key={a.id} artwork={a} canEdit={canEdit}/>)}
                  </div>
                )}
                {totalPages > 1 && (
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, marginTop:20 }}>
                    <button onClick={() => setPageByCol(p=>({...p,[col.id]:Math.max(0,page-1)}))} disabled={page===0}
                      style={{ padding:"6px 12px", border:`1px solid ${C.border}`, background:C.bg, cursor:page===0?"not-allowed":"pointer", color:page===0?C.caption:C.black, fontSize:13 }}>
                      <ChevronLeft size={13}/>
                    </button>
                    <span style={{ fontSize:13, color:C.muted }}>{t("delegatedAccess.page", { current: page + 1, total: totalPages })}</span>
                    <button onClick={() => setPageByCol(p=>({...p,[col.id]:Math.min(totalPages-1,page+1)}))} disabled={page>=totalPages-1}
                      style={{ padding:"6px 12px", border:`1px solid ${C.border}`, background:C.bg, cursor:page>=totalPages-1?"not-allowed":"pointer", color:page>=totalPages-1?C.caption:C.black, fontSize:13 }}>
                      <ChevronRight size={13}/>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Onglet Toutes les oeuvres ─────────────────────────────
function TabArtworks({ collections, collabEntry }) {
  const { t } = useTranslation();
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [loaded,  setLoaded]    = useState(false);
  const [search,  setSearch]    = useState("");
  const [filterArtist, setFilterArtist] = useState("");
  const [filterMedium, setFilterMedium] = useState("");
  const [page,    setPage]      = useState(0);
  const canEdit = collabEntry?.can_edit === true;

  useEffect(() => {
    if (loaded || !collections.length) return;
    setLoading(true);
    Promise.all(collections.map(col =>
      supabase.rpc("get_collaborator_artworks", {
        p_collaborator_id: collabEntry.id,
        p_collection_id: col.id,
      }).then(r => r.data||[])
    )).then(results => {
      setArtworks(results.flat());
      setLoaded(true);
      setLoading(false);
    });
  }, [loaded, collections, collabEntry?.id]);

  const artists = useMemo(() => [...new Set(artworks.map(a=>a.artist).filter(Boolean))].sort(), [artworks]);
  const mediums = useMemo(() => [...new Set(artworks.map(a=>a.medium).filter(Boolean))].sort(), [artworks]);

  const filtered = useMemo(() => {
    let r = artworks;
    if (search.trim()) { const q=search.toLowerCase(); r=r.filter(a=>(a.title||"").toLowerCase().includes(q)||(a.artist||"").toLowerCase().includes(q)); }
    if (filterArtist) r=r.filter(a=>a.artist===filterArtist);
    if (filterMedium) r=r.filter(a=>a.medium===filterMedium);
    return r;
  }, [artworks, search, filterArtist, filterMedium]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems  = filtered.slice(page*PAGE_SIZE, (page+1)*PAGE_SIZE);
  useEffect(() => { setPage(0); }, [search, filterArtist, filterMedium]);

  const selStyle = { padding:"7px 10px", border:`1px solid ${C.border}`, fontSize:13,
    color:C.black, background:C.bg, outline:"none", fontFamily:"system-ui,sans-serif", cursor:"pointer" };

  return (
    <div>
      {loading && <div style={{ display:"flex", alignItems:"center", gap:8, color:C.muted, fontSize:13, padding:"32px 0" }}><RefreshCw size={14} style={{ animation:"spin 1s linear infinite" }}/> {t("delegatedAccess.allArtworksLoading")}</div>}
      {loaded && (
        <>
          {/* Filtres */}
          <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:20, alignItems:"center" }}>
            <div style={{ position:"relative", flex:"1 1 200px", minWidth:160 }}>
              <Search size={13} color={C.caption} style={{ position:"absolute", left:9, top:"50%", transform:"translateY(-50%)" }}/>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={t("delegatedAccess.search")}
                style={{ ...selStyle, width:"100%", paddingLeft:28, boxSizing:"border-box" }}/>
            </div>
            {artists.length > 1 && (
              <select value={filterArtist} onChange={e=>setFilterArtist(e.target.value)} style={{ ...selStyle, flex:"1 1 160px", minWidth:140 }}>
                <option value="">{t("delegatedAccess.allArtists")}</option>
                {artists.map(a=><option key={a} value={a}>{a}</option>)}
              </select>
            )}
            {mediums.length > 1 && (
              <select value={filterMedium} onChange={e=>setFilterMedium(e.target.value)} style={{ ...selStyle, flex:"1 1 140px", minWidth:120 }}>
                <option value="">{t("delegatedAccess.allMediums")}</option>
                {mediums.map(m=><option key={m} value={m}>{m}</option>)}
              </select>
            )}
            {(search||filterArtist||filterMedium) && (
              <button onClick={()=>{setSearch("");setFilterArtist("");setFilterMedium("");}}
                style={{ ...selStyle, color:C.error, borderColor:C.error, background:"none", display:"flex", alignItems:"center", gap:4 }}>
                <X size={12}/> {t("delegatedAccess.clear")}
              </button>
            )}
            <span style={{ fontSize:12, color:C.caption, marginLeft:"auto" }}>
              {t("delegatedAccess.results", { count: filtered.length })}{filtered.length!==artworks.length?` / ${artworks.length}`:""}
            </span>
          </div>

          {filtered.length===0 && <p style={{ fontSize:13, color:C.caption, textAlign:"center", padding:"32px 0" }}>{t("delegatedAccess.noArtwork")}</p>}

          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:14 }}>
            {pageItems.map(a=><ArtworkCard key={a.id} artwork={a} canEdit={canEdit}/>)}
          </div>

          {totalPages > 1 && (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, marginTop:24 }}>
              <button onClick={()=>setPage(p=>Math.max(0,p-1))} disabled={page===0}
                style={{ padding:"7px 14px", border:`1px solid ${C.border}`, background:C.bg, cursor:page===0?"not-allowed":"pointer", color:page===0?C.caption:C.black, fontSize:13, display:"flex", alignItems:"center", gap:4 }}>
                <ChevronLeft size={14}/> {t("delegatedAccess.previous")}
              </button>
              <span style={{ fontSize:13, color:C.muted }}>{t("delegatedAccess.page", { current: page + 1, total: totalPages })}</span>
              <button onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))} disabled={page>=totalPages-1}
                style={{ padding:"7px 14px", border:`1px solid ${C.border}`, background:C.bg, cursor:page>=totalPages-1?"not-allowed":"pointer", color:page>=totalPages-1?C.caption:C.black, fontSize:13, display:"flex", alignItems:"center", gap:4 }}>
                {t("delegatedAccess.next")} <ChevronRight size={14}/>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Onglet Emplacements ───────────────────────────────────
function TabLocations({ collabEntry }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.rpc("get_collaborator_artworks_all", { p_collaborator_id: collabEntry.id })
      .then(({ data }) => { setArtworks(data||[]); setLoading(false); })
      .catch(() => setLoading(false));
  }, [collabEntry?.id]);

  const byLocation = useMemo(() => {
    const map = {};
    artworks.forEach(a => {
      const loc = a.location_label || a.location_name || t("delegatedAccess.unknownLocation");
      if (!map[loc]) map[loc] = [];
      map[loc].push(a);
    });
    return map;
  }, [artworks, t]);

  if (loading) return <div style={{ padding:"32px 0", color:C.muted, fontSize:13, display:"flex", alignItems:"center", gap:8 }}><RefreshCw size={14} style={{ animation:"spin 1s linear infinite" }}/> {t("delegatedAccess.loading")}</div>;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
      {Object.entries(byLocation).map(([loc, items]) => (
        <div key={loc} style={{ background:C.white, border:`1px solid ${C.border}`, padding:"16px 24px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
            <MapPin size={16} color={C.gold}/>
            <p style={{ fontFamily:"Georgia,serif", fontSize:15, color:C.black, margin:0 }}>{loc}</p>
            <span style={{ fontSize:12, color:C.caption }}>— {t("delegatedAccess.artworkCount", { count: items.length })}</span>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))", gap:10 }}>
            {items.map(a => (
              <div key={a.id} onClick={() => navigate(`/artwork/${a.id}`)}
                style={{ cursor:"pointer", display:"flex", alignItems:"center", gap:8,
                  padding:"6px 8px", border:`1px solid ${C.border}`, background:C.bg }}>
                <div style={{ width:36, height:36, flexShrink:0, background:"#e5e3df", overflow:"hidden" }}>
                  <img src={pickImage(a)||ARTWORK_FALLBACK} alt={a.title||""} onError={e=>{e.target.src=ARTWORK_FALLBACK;}}
                    style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                </div>
                <p style={{ fontSize:11, color:C.black, margin:0, overflow:"hidden",
                  whiteSpace:"nowrap", textOverflow:"ellipsis", fontStyle:"italic" }}>
                  {a.title || t("delegatedAccess.untitled")}
                </p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Onglet Rapports ───────────────────────────────────────
function TabReports({ collabEntry }) {
  const { t } = useTranslation();
  const canReports = collabEntry?.can_generate_reports === true;
  if (!canReports) return (
    <div style={{ padding:"48px 0", textAlign:"center", color:C.caption }}>
      <FileText size={28} style={{ marginBottom:12, opacity:0.4 }}/>
      <p style={{ fontFamily:"Georgia,serif", fontSize:17, color:C.black, marginBottom:8 }}>
        {t("delegatedAccess.reportsPermissionTitle")}
      </p>
      <p style={{ fontSize:13, lineHeight:1.7 }}>
        {t("delegatedAccess.reportsPermissionBody")}
      </p>
    </div>
  );
  return (
    <div style={{ padding:"32px 0" }}>
      <p style={{ fontSize:14, color:C.muted, marginBottom:24, lineHeight:1.7 }}>
        {t("delegatedAccess.reportsIntro")}
      </p>
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {[
          { label:t("delegatedAccess.reportInventory"), href:"/expert/dashboard?tab=outils", desc:t("delegatedAccess.reportInventoryDesc") },
          { label:t("delegatedAccess.reportValuation"), href:"/expert/dashboard?tab=outils", desc:t("delegatedAccess.reportValuationDesc") },
        ].map(r => (
          <a key={r.label} href={r.href}
            style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
              padding:"16px 20px", background:C.white, border:`1px solid ${C.border}`,
              textDecoration:"none", color:C.black }}>
            <div>
              <p style={{ fontFamily:"Georgia,serif", fontSize:15, margin:"0 0 3px" }}>{r.label}</p>
              <p style={{ fontSize:12, color:C.muted, margin:0 }}>{r.desc}</p>
            </div>
            <ChevronRight size={16} color={C.caption}/>
          </a>
        ))}
      </div>
    </div>
  );
}

// ── Onglet Vision IA ──────────────────────────────────────
function TabVision({ collabEntry }) {
  const { t } = useTranslation();
  const canAI = collabEntry?.can_access_ai === true;
  if (!canAI) return (
    <div style={{ padding:"48px 0", textAlign:"center", color:C.caption }}>
      <Cpu size={28} style={{ marginBottom:12, opacity:0.4 }}/>
      <p style={{ fontFamily:"Georgia,serif", fontSize:17, color:C.black, marginBottom:8 }}>
        {t("delegatedAccess.reportsPermissionTitle")}
      </p>
      <p style={{ fontSize:13, lineHeight:1.7 }}>
        {t("delegatedAccess.visionPermissionBody")}
      </p>
    </div>
  );
  return (
    <div style={{ padding:"32px 0" }}>
      <p style={{ fontSize:14, color:C.muted, marginBottom:24, lineHeight:1.7 }}>
        {t("delegatedAccess.visionIntro")}
      </p>
      <button type="button" onClick={() => openVision()}
        style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"12px 20px",
          background:C.black, color:C.white, border:"none", cursor:"pointer", fontSize:14,
          fontFamily:"system-ui,sans-serif", fontWeight:600 }}>
        <Cpu size={16}/> {t("delegatedAccess.openVision")}
      </button>
    </div>
  );
}

// ── Dashboard principal ───────────────────────────────────
export default function DelegatedAccessDashboard() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading]           = useState(true);
  const [error,   setError]             = useState(null);
  const [collabEntry, setCollabEntry]   = useState(null);
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [collections, setCollections]   = useState([]);
  const [activeTab,   setActiveTab]     = useState("collections");

  const loadData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true); setError(null);
    try {
      let collab = null;
      const { data: byUid } = await supabase.from("collaborators").select("*")
        .eq("user_id", user.id).eq("status","active").limit(1).maybeSingle();
      collab = byUid;

      if (!collab && user.email) {
        const { data: byEmail } = await supabase.from("collaborators").select("*")
          .eq("email", user.email).in("status",["active","pending"]).limit(1).maybeSingle();
        if (byEmail) {
          if (byEmail.status === "pending") {
            await supabase.from("collaborators").update({
              user_id: user.id, status:"active",
              invitation_accepted_at: new Date().toISOString(),
            }).eq("id", byEmail.id);
            collab = { ...byEmail, user_id:user.id, status:"active" };
          } else { collab = byEmail; }
        }
      }

      if (!collab) { setError(t("delegatedAccess.noAccess")); setLoading(false); return; }
      setCollabEntry(collab);
      // Signale au Header que cet utilisateur est un collaborateur délégué
      // (persiste sur toutes les routes, ex: /artwork/:id)
      try { localStorage.setItem("delegated_context", "1"); } catch (_) {}

      if (collab.created_by) {
        const { data: owner } = await supabase.from("profiles")
          .select("id,first_name,last_name,company,role,plan")
          .eq("id", collab.created_by).maybeSingle();
        setOwnerProfile(owner);
      }

      const { data: rpcCols, error: rpcErr } = await supabase
        .rpc("get_collaborator_collections", { p_collaborator_id: collab.id });

      if (rpcErr) {
        let cols = [];
        if (collab.collection_id) {
          const { data } = await supabase.from("collections")
            .select("id,title,created_at").eq("id", collab.collection_id).maybeSingle();
          if (data) cols = [data];
        }
        setCollections(cols);
      } else { setCollections(rpcCols||[]); }
    } catch(e) { setError(e.message || t("delegatedAccess.genericError")); }
    finally { setLoading(false); }
  }, [user?.id, user?.email, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const can = p => collabEntry?.[p] === true;
  const ownerName = ownerProfile
    ? (ownerProfile.company || `${ownerProfile.first_name||""} ${ownerProfile.last_name||""}`.trim() || t("delegatedAccess.ownerFallback"))
    : t("delegatedAccess.ownerFallback");
  const roleLabel = t(`delegatedAccess.roles.${ROLE_KEYS[collabEntry?.role] || "collaborator"}`);
  const handleLogout = async () => {
    try { localStorage.removeItem("delegated_context"); } catch (_) {}
    await signOut();
    navigate("/login", { replace: true });
  };

  if (loading) return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <p style={{ fontFamily:"Georgia,serif", color:C.muted }}>{t("delegatedAccess.loadingPage")}</p>
    </div>
  );

  if (error) return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ maxWidth:480, width:"100%", background:C.white, border:`1px solid ${C.border}`, padding:32 }}>
        <AlertCircle size={24} color={C.error} style={{ marginBottom:12 }}/>
        <h2 style={{ fontFamily:"Georgia,serif", fontWeight:400, fontSize:20, color:C.black, marginBottom:8 }}>{t("delegatedAccess.accessUnavailable")}</h2>
        <p style={{ fontSize:14, color:C.muted, lineHeight:1.7, marginBottom:20 }}>{error}</p>
        <button onClick={handleLogout} style={{ fontSize:13, color:C.muted, background:"none", border:`1px solid ${C.border}`, padding:"8px 16px", cursor:"pointer" }}>{t("delegatedAccess.logout")}</button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:C.bg, fontFamily:"system-ui,-apple-system,sans-serif" }}>
      {/* Header */}
      <header style={{ background:C.white, borderBottom:`1px solid ${C.border}`,
        padding:"0 32px", height:56, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:20 }}>
          <span style={{ fontFamily:"Georgia,serif", fontSize:17, color:C.black, letterSpacing:"0.1em" }}>ARTKIV</span>
          <span style={{ fontSize:12, color:C.caption, borderLeft:`1px solid ${C.border}`, paddingLeft:16 }}>
            {roleLabel} · <strong style={{ color:C.black }}>{ownerName}</strong>
          </span>
        </div>
        <button onClick={handleLogout} style={{ display:"flex", alignItems:"center", gap:6,
          fontSize:13, color:C.muted, background:"none", border:"none", cursor:"pointer" }}>
          <LogOut size={14}/> {t("delegatedAccess.logoutShort")}
        </button>
      </header>

      {/* Bandeau permissions */}
      <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`,
        padding:"10px 32px", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
        <span style={{ fontSize:11, color:C.caption, marginRight:4 }}>{t("delegatedAccess.access")}</span>
        <PermBadge icon={<Eye size={11}/>}       label={t("delegatedAccess.permissions.view")}      active={can("can_view")}/>
        <PermBadge icon={<Edit3 size={11}/>}     label={t("delegatedAccess.permissions.edit")}    active={can("can_edit")}/>
        <PermBadge icon={<Plus size={11}/>}      label={t("delegatedAccess.permissions.add")}   active={can("can_add_artworks")}/>
        <PermBadge icon={<Trash2 size={11}/>}    label={t("delegatedAccess.permissions.delete")} active={can("can_delete_artworks")}/>
        <PermBadge icon={<Download size={11}/>}  label={t("delegatedAccess.permissions.export")}  active={can("can_export")}/>
        <PermBadge icon={<BarChart2 size={11}/>} label={t("delegatedAccess.permissions.reports")}  active={can("can_generate_reports")}/>
        <PermBadge icon={<Cpu size={11}/>}       label={t("delegatedAccess.permissions.ai")}        active={can("can_access_ai")}/>
      </div>

      {/* Navigation onglets */}
      <nav style={{ background:C.white, borderBottom:`1px solid ${C.border}`, padding:"0 32px",
        display:"flex", gap:0, overflowX:"auto" }}>
        {TABS.map(({ key, labelKey, icon: Icon }) => (
          <button key={key} type="button" onClick={() => setActiveTab(key)}
            style={{ display:"flex", alignItems:"center", gap:6, padding:"14px 20px",
              background:"none", border:"none", borderBottom: activeTab===key ? `2px solid ${C.black}` : "2px solid transparent",
              cursor:"pointer", fontSize:13, color: activeTab===key ? C.black : C.muted,
              fontWeight: activeTab===key ? 600 : 400, whiteSpace:"nowrap",
              fontFamily:"system-ui,sans-serif" }}>
            <Icon size={14}/> {t(labelKey)}
          </button>
        ))}
      </nav>

      {/* Contenu */}
      <div style={{ maxWidth:1100, margin:"0 auto", padding:"32px 24px" }}>
        {activeTab === "collections" && <TabCollections collections={collections} collabEntry={collabEntry}/>}
        {activeTab === "artworks"    && <TabArtworks collections={collections} collabEntry={collabEntry}/>}
        {activeTab === "locations"   && <TabLocations collabEntry={collabEntry}/>}
        {activeTab === "reports"     && <TabReports collabEntry={collabEntry}/>}
        {activeTab === "vision"      && <TabVision collabEntry={collabEntry}/>}
      </div>

      <div style={{ maxWidth:1100, margin:"0 auto", padding:"0 24px 32px" }}>
        <div style={{ paddingTop:20, borderTop:`1px solid ${C.border}` }}>
          <p style={{ fontSize:12, color:C.caption, lineHeight:1.7 }}>
            {t("delegatedAccess.footer", { owner: ownerName })}
          </p>
        </div>
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}